import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Input, Select, Spin, Switch } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { useDebouncedValue } from 'services/hooks';
import {
  Colors,
  EmploymentTypeOptions,
  SeniorityOptions,
  SortOptions,
  WorkModeOptions,
} from 'context';
import { ToastTypes } from 'atoms';
import { CareerVacancyCard, CareerVacancyDetails } from 'molecules';
import {
  formatSalaryRange,
  getCareerErrorText,
  useCareerFilterDictionaries,
  useCareerProfile,
  useCareerVacancyFeed,
  useMarkVacancyInteraction,
  useMyCareerCenter,
  useToaster,
} from 'core';
import { PATHS } from 'router/config';

import { CareerPreviewNotice } from './CareerPreviewNotice';
import { useCareerAccessGuard } from './useCareerAccessGuard';

const INTERACTION_TOAST_KEY = 'careerVacancyInteraction';

/**
 * /career/vacancies — the member's vacancy feed (spec 0018 §B2).
 *
 * Filters (direction / work mode / seniority / debounced search) drive the
 * server-side query; every row's Save / Apply state is server truth
 * (`interaction`), and the mark mutation updates the normalized cache in place
 * so a click reflects without a refetch.
 */
export const CareerVacancies = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToaster();

  // Guard mirrors the other /career pages: a non-member hitting the URL is
  // bounced to 404, and the server refuses the data independently anyway.
  const { isPreview } = useCareerAccessGuard();

  /**
   * Лента открывается СУЖЕННОЙ: направление, которому человек учится, и его
   * уровень. Владелец: «пусть изначально будет указано направление, в которое
   * студент учится, и уровень джуниорский».
   *
   * 🔴 Оба значения уже есть, ничего нового считать не надо: направление —
   * `myCareerCenter.directionId` (методист проставил или выведено из курса
   * группы), уровень — `CareerProfile.seniority`. Замерено на стенде: из 14
   * вакансий остаётся 9, то есть сужение не превращает ленту в пустой экран.
   *
   * ⚠️ Сужение обязано быть ВИДИМЫМ и снимаемым одним нажатием — иначе человек
   * решит, что вакансий всего девять. Плашка с «показать все» ниже.
   */
  const { center, loading: centerLoading } = useMyCareerCenter();
  const { profile, loading: profileLoading } = useCareerProfile();
  const defaultsRef = useRef(false);

  const [directionId, setDirectionId] = useState<string | undefined>();
  const [cityId, setCityId] = useState<string | undefined>();
  const [workMode, setWorkMode] = useState<string | undefined>();
  const [seniority, setSeniority] = useState<string | undefined>();
  /** Подставили ли мы фильтры сами — от этого зависит подпись над лентой. */
  const [autoFiltered, setAutoFiltered] = useState(false);
  const [employmentType, setEmploymentType] = useState<string | undefined>();
  const [skillIds, setSkillIds] = useState<string[]>([]);
  const [sort, setSort] = useState<string>('relevance');
  const [search, setSearch] = useState('');
  const [savedOnly, setSavedOnly] = useState(false);
  // id открытой карточки; подробный вид читает её из уже загруженного списка,
  // поэтому отдельного запроса не нужно.
  const [detailsId, setDetailsId] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search);

  /**
   * Подставляем ОДИН раз и только пока человек ничего не трогал.
   *
   * 🔴 Ждём ОБА источника, а не первый пришедший. Членство и профиль — разные
   * запросы, и профиль приезжает позже; закрыв подстановку по первому ответу,
   * я получил ленту с направлением, но БЕЗ уровня — поймано глазами на стенде,
   * «Znaleziono: 11» вместо 9.
   */
  useEffect(() => {
    if (defaultsRef.current) return;
    if (centerLoading || profileLoading) return;

    const ownDirection = center?.directionId ?? null;
    const ownSeniority = profile?.values.seniority || '';

    defaultsRef.current = true;
    if (!ownDirection && !ownSeniority) return;

    if (ownDirection) setDirectionId(ownDirection);
    if (ownSeniority) setSeniority(ownSeniority);
    setAutoFiltered(true);
  }, [center, centerLoading, profile, profileLoading]);

  const dropAutoFilters = useCallback(() => {
    setDirectionId(undefined);
    setSeniority(undefined);
    setAutoFiltered(false);
  }, []);

  const {
    vacancies,
    totalCount,
    totalCountCapped,
    relevanceApplied,
    hasMore,
    loading,
    error,
    loadMore,
  } = useCareerVacancyFeed({
    directionId,
    cityId,
    workMode,
    seniority,
    employmentType,
    skillIds,
    sort,
    search: debouncedSearch,
    savedOnly,
  });
  const { directionOptions, cityOptions } = useCareerFilterDictionaries();

  // Клик по чипу навыка на карточке — это и есть основной вход в фильтр по
  // навыкам: аргумент `skillId` существовал в запросе с самого начала, но ни
  // один элемент интерфейса его не задавал.
  const toggleSkill = useCallback((skillId: string) => {
    setSkillIds((prev) =>
      prev.includes(skillId) ? prev.filter((id) => id !== skillId) : [...prev, skillId],
    );
  }, []);

  const { markInteraction } = useMarkVacancyInteraction();
  // Per-vacancy in-flight set: a click on card B must not clear card A's
  // pending state, and both of a card's buttons stay disabled until its own
  // request resolves (guards the concurrent Save-then-Apply cache regression).
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const handleInteraction = useCallback(
    async (vacancyId: string, kind: 'saved' | 'applied') => {
      // Ранний выход, а не только скрытые кнопки: точек вызова три, и
      // забытая четвёртая молча ушла бы к серверу за отказом.
      if (isPreview) {
        return;
      }
      setPendingIds((prev) => new Set(prev).add(vacancyId));
      try {
        const result = await markInteraction(vacancyId, kind);
        if (!result.success) {
          addToast(INTERACTION_TOAST_KEY, {
            type: ToastTypes.danger,
            // По КОДУ, а не по тексту сервера: сообщение сервера русское.
            text: getCareerErrorText(t, result.errorCode),
          });
        }
      } catch {
        addToast(INTERACTION_TOAST_KEY, {
          type: ToastTypes.danger,
          text: t('careerCenter.vacancies.actionError'),
        });
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(vacancyId);
          return next;
        });
      }
    },
    [markInteraction, addToast, t, isPreview],
  );

  const hasActiveFilters = Boolean(
    directionId ||
      cityId ||
      workMode ||
      seniority ||
      employmentType ||
      skillIds.length ||
      debouncedSearch.trim() ||
      savedOnly,
  );
  // «Только сохранённые» — это режим, а не фильтр, и пустой результат у него
  // значит совсем другое: не «под фильтры ничего не нашлось», а «вы пока
  // ничего не сохранили». Разводим, иначе экран врёт.
  const savedOnlyIsTheOnlyFilter =
    savedOnly &&
    !directionId &&
    !cityId &&
    !workMode &&
    !seniority &&
    !employmentType &&
    !skillIds.length &&
    !debouncedSearch.trim();

  const resetFilters = useCallback(() => {
    setDirectionId(undefined);
    setCityId(undefined);
    setWorkMode(undefined);
    setSeniority(undefined);
    setEmploymentType(undefined);
    setSkillIds([]);
    setSearch('');
    setSavedOnly(false);
  }, []);

  return (
    <PageWrapper>
      <BackLink type="button" onClick={() => navigate(PATHS.careerCenter)}>
        ← {t('careerCenter.vacancies.back')}
      </BackLink>

      {isPreview ? <CareerPreviewNotice /> : null}

      <PageHeader>
        <PageTitle>{t('careerCenter.vacancies.title')}</PageTitle>
        <PageInfo>{t('careerCenter.vacancies.info')}</PageInfo>
      </PageHeader>

      <FiltersBar>
        {directionOptions.length ? (
          <FilterSelect
            allowClear
            value={directionId}
            placeholder={t('careerCenter.vacancies.filters.direction')}
            options={directionOptions}
            onChange={(value) => setDirectionId((value as string) || undefined)}
          />
        ) : null}

        <FilterSelect
          allowClear
          showSearch
          optionFilterProp="label"
          value={cityId}
          placeholder={t('careerCenter.vacancies.filters.city')}
          options={cityOptions}
          onChange={(value) => setCityId((value as string) || undefined)}
        />

        <FilterSelect
          allowClear
          value={employmentType}
          placeholder={t('careerCenter.vacancies.filters.employment')}
          options={EmploymentTypeOptions}
          onChange={(value) => setEmploymentType((value as string) || undefined)}
        />

        <FilterSelect
          value={sort}
          placeholder={t('careerCenter.vacancies.sort.label')}
          options={SortOptions}
          onChange={(value) => setSort((value as string) || 'relevance')}
        />

        <FilterSelect
          allowClear
          value={workMode}
          placeholder={t('careerCenter.vacancies.filters.workMode')}
          options={WorkModeOptions}
          onChange={(value) => setWorkMode((value as string) || undefined)}
        />

        <FilterSelect
          allowClear
          value={seniority}
          placeholder={t('careerCenter.vacancies.filters.seniority')}
          options={SeniorityOptions}
          onChange={(value) => setSeniority((value as string) || undefined)}
        />

        <SearchInput
          allowClear
          value={search}
          placeholder={t('careerCenter.vacancies.filters.search')}
          onChange={(event) => setSearch(event.target.value)}
        />

        {/* «Только сохранённые» в предпросмотре — заведомо пустой фильтр:
            сохранять смотрящему негде. Показать его значит подсунуть кнопку,
            которая всегда даёт пустой экран. */}
        {isPreview ? null : (
          <SavedFilter>
            <Switch size="small" checked={savedOnly} onChange={setSavedOnly} />
            {t('careerCenter.vacancies.filters.savedOnly')}
          </SavedFilter>
        )}
      </FiltersBar>

      {/* Сужение обязано быть видимым: без этой строки человек решит, что
          вакансий в школе всего девять, и уйдёт. Кнопка снимает ровно два
          фильтра, которые подставили мы, а не те, что он выбрал сам. */}
      {autoFiltered && !error ? (
        <AutoFilterNotice>
          <span>{t('careerCenter.vacancies.autoFiltered')}</span>
          <LinkButton type="button" onClick={dropAutoFilters}>
            {t('careerCenter.vacancies.autoFilteredShowAll')}
          </LinkButton>
        </AutoFilterNotice>
      ) : null}

      {!error && vacancies.length ? (
        <ResultsLine>
          <ResultsCount>
            {t('careerCenter.vacancies.count', {
              n: totalCountCapped ? `${totalCount}+` : totalCount,
            })}
          </ResultsCount>
          {/* 🔴 Ни слова о подборе, пока сервер не подтвердил, что ранжирование
              действительно применилось. Иначе интерфейс обещает «подобрано для
              вас» поверх обычной сортировки по дате. */}
          {sort === 'relevance' ? (
            <RelevanceHint>
              {relevanceApplied
                ? t('careerCenter.vacancies.relevanceOn')
                : t('careerCenter.vacancies.relevanceOff')}
            </RelevanceHint>
          ) : null}
          {/* Сброс живёт ЗДЕСЬ, а не только на пустом экране. Чипы навыков
              кликабельны, фильтров семь, и студент, набравший четыре, мог
              вернуться назад только вспомнив каждый и сняв его вручную —
              а увидеть выход он мог, лишь доведя выборку до нуля. */}
          {hasActiveFilters ? (
            <InlineResetButton type="button" onClick={resetFilters}>
              {t('careerCenter.vacancies.resetFilters')}
            </InlineResetButton>
          ) : null}
        </ResultsLine>
      ) : null}

      {error ? (
        <Alert type="error" showIcon message={t('careerCenter.vacancies.loadError')} />
      ) : loading && !vacancies.length ? (
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      ) : vacancies.length ? (
        <VacancyList>
          {vacancies.map((vacancy) => (
            <CareerVacancyCard
              key={vacancy.id}
              title={vacancy.title}
              companyName={vacancy.companyName}
              location={vacancy.location}
              workMode={vacancy.workMode}
              seniority={vacancy.seniority}
              employmentType={vacancy.employmentType}
              salary={formatSalaryRange(
                vacancy.salaryMin,
                vacancy.salaryMax,
                vacancy.salaryCurrency,
              )}
              description={vacancy.descriptionShort}
              directionTitles={vacancy.directions.map((direction) => direction.title)}
              skills={vacancy.skills}
              activeSkillIds={skillIds}
              onSkillClick={toggleSkill}
              matchedSkillCount={vacancy.matchedSkillCount}
              isNew={vacancy.isNew}
              applyUrl={vacancy.applyUrl}
              expiresAt={vacancy.expiresAt}
              publishedAt={vacancy.publishedAt}
              interaction={vacancy.interaction}
              canSave={!isPreview}
              actionLoading={pendingIds.has(vacancy.id)}
              onSave={() => handleInteraction(vacancy.id, 'saved')}
              onOpenDetails={() => setDetailsId(vacancy.id)}
            />
          ))}
        </VacancyList>
      ) : (
        <EmptyState>
          {savedOnlyIsTheOnlyFilter
            ? t('careerCenter.vacancies.emptySaved')
            : hasActiveFilters
            ? t('careerCenter.vacancies.emptyFiltered')
            : t('careerCenter.vacancies.empty')}
          {/* Кнопка называлась «сбросить фильтры», а звала refetch() — то есть
              перезапускала тот же пустой запрос. Теперь она действительно
              сбрасывает; «обновить» осталось только на экране ошибки, где оно
              и уместно. */}
          {hasActiveFilters ? (
            <ResetButton type="button" onClick={resetFilters}>
              {savedOnlyIsTheOnlyFilter
                ? t('careerCenter.vacancies.emptySavedAction')
                : t('careerCenter.vacancies.resetFilters')}
            </ResetButton>
          ) : null}
        </EmptyState>
      )}

      {hasMore && !error ? (
        <LoadMoreRow>
          <LoadMoreButton type="button" disabled={loading} onClick={loadMore}>
            {t('careerCenter.vacancies.loadMore')}
          </LoadMoreButton>
        </LoadMoreRow>
      ) : null}

      <CareerVacancyDetails
        vacancyId={detailsId}
        onClose={() => setDetailsId(null)}
        onSave={() => (detailsId ? handleInteraction(detailsId, 'saved') : undefined)}
        canSave={!isPreview}
        actionLoading={Boolean(detailsId && pendingIds.has(detailsId))}
      />
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const BackLink = styled.button`
  margin-top: 32px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
    color: ${Colors.black};
  }
`;

const PageHeader = styled.div`
  margin: 16px 0 24px 0;
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const FiltersBar = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
  margin-bottom: 24px;

  ${tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FilterSelect = styled(Select)`
  width: 100%;
` as typeof Select;

const SearchInput = styled(Input)`
  width: 100%;
`;

const VacancyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 48px 0;
  text-align: center;
  color: ${Colors.gray_dark};
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.regular })}
`;

const ResetButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

/** Тот же сброс, что на пустом экране, но в строке результатов: там он нужен
 *  ДО того, как выборка схлопнется в ноль. */
const InlineResetButton = styled(ResetButton)`
  margin-left: auto;
`;

const SavedFilter = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${Colors.gray_dark};
  cursor: pointer;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const AutoFilterNotice = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin: 12px 0 4px;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${Colors.bg_gray};
  color: ${Colors.black};
  ${fontService.text({ type: TextType.small })}
`;

const LinkButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-decoration: underline;
  color: ${Colors.black};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const ResultsLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
  margin-bottom: 12px;
`;

const ResultsCount = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.getFont({ size: 14, lineHeight: 143, weight: FontWeight.medium })}
`;

const RelevanceHint = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.getFont({ size: 13, lineHeight: 146 })}
`;

const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

const LoadMoreButton = styled.button`
  padding: 10px 24px;
  border: 1px solid ${Colors.main};
  border-radius: 8px;
  background: transparent;
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }

  ${fontService.getFont({ size: 14, lineHeight: 143, weight: FontWeight.medium })}
`;
